import styles from 'css/src/components/common/DashboardProjectItem.module.scss';

import React from 'react';
import type { ConnectedProps } from 'react-redux';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';

import ProjectIcon from 'js/src/components/dashboard/ProjectIcon';
import {
    resetSearchedVideos,
    selectViewColor,
    selectViewIcon,
    selectViewName,
} from 'js/src/data/dashboard';
import type { AppDispatch, RootState } from 'js/src/libs/RootState';

type PropsFromRedux = ConnectedProps<typeof connector>;

type OwnProps = {
    projectId: number;
    onProjectClick: (projectId: number) => void;
    activeView?: number;
};

const DashboardProjectItemPresentation: React.FC<PropsFromRedux & OwnProps> = React.memo(
    function DashboardProjectItemPresentation({
        projectId,
        name,
        color,
        icon,
        onProjectClick,
        activeView,
        resetSearchedVideos,
    }) {
        const isActive = projectId === activeView;
        const activeClassName = isActive ? styles['active-project'] : '';

        const handleClick = React.useCallback(
            (e: React.MouseEvent) => {
                resetSearchedVideos();
                onProjectClick(projectId);
            },
            [projectId, resetSearchedVideos, onProjectClick],
        );

        return (
            <li
                className={`${styles['project-list-item']} ${activeClassName}`}
                key={projectId}
                data-project-list-item=""
                data-testid={isActive ? 'active-project' : undefined}
            >
                <a
                    role="button"
                    onClick={handleClick}
                    data-testid="project-anchor"
                    className={styles['project-list-item-link']}
                >
                    <ProjectIcon iconName={icon} iconBgColor={color} />
                    <span className="text-truncate">{name}</span>
                </a>
            </li>
        );
    },
);

const mapStateToProps = (state: RootState, ownProps: OwnProps) => {
    return {
        name: selectViewName(state, ownProps.projectId),
        color: selectViewColor(state, ownProps.projectId),
        icon: selectViewIcon(state, ownProps.projectId),
    };
};

const mapDispatchToProps = (dispatch: AppDispatch) => {
    return bindActionCreators(
        {
            resetSearchedVideos,
        },
        dispatch,
    );
};

const connector = connect(mapStateToProps, mapDispatchToProps);

const DashboardProjectItem = connector(DashboardProjectItemPresentation);

export default DashboardProjectItem;
